import { createStart, createMiddleware } from "@tanstack/react-start";

import { renderErrorPage } from "./lib/error-page";
import { logServerError } from "./lib/error-logging";
import { attachSupabaseAuth } from "@/integrations/supabase/auth-attacher";

import { brand } from "@/config/brand";
const SITE_URL = brand.url;

/**
 * Web Linking (RFC 8288) response headers for agent / crawler discovery.
 *
 * These advertise machine-readable entry points so AI agents and tools can
 * discover the site's API catalog, plain-text context document and sitemap
 * straight from the HTTP response, without parsing the HTML first.
 *
 *   - rel="api-catalog"  -> RFC 9727 linkset describing available endpoints
 *   - rel="service-doc"  -> human/agent-readable docs (our llms.txt)
 *   - rel="sitemap"      -> standard XML sitemap
 */
const LINK_HEADER = [
  `<${SITE_URL}/.well-known/api-catalog>; rel="api-catalog"; type="application/linkset+json"`,
  `<${SITE_URL}/llms.txt>; rel="service-doc"; type="text/plain"`,
  `<${SITE_URL}/sitemap.xml>; rel="sitemap"; type="application/xml"`,
].join(", ");

/**
 * Appends the discovery Link header to every HTML document response (which
 * includes the homepage). Non-HTML responses (assets, JSON APIs) are left
 * untouched. Failures here must never break a page render, so everything is
 * guarded.
 */
const agentDiscoveryMiddleware = createMiddleware().server(async ({ next }) => {
  const result = await next();
  try {
    // Request middleware normally resolves to a result object with a
    // `.response`, but error handling can return a bare Response — handle both.
    const response =
      result instanceof Response
        ? result
        : (result as { response?: Response } | undefined)?.response;
    if (response instanceof Response) {
      const contentType = response.headers.get("content-type") ?? "";
      if (contentType.includes("text/html")) {
        response.headers.append("Link", LINK_HEADER);
      }
    }
  } catch {
    // Header mutation is best-effort; never block the response.
  }
  return result;
});

/**
 * Baseline security headers applied to every HTML document response.
 *
 * Notes on what's already handled by the hosting layer (Cloudflare in front of
 * lovable.app / the custom domain) versus what we add here:
 *   - Strict-Transport-Security: set by hosting
 *   - Referrer-Policy: set by hosting (strict-origin-when-cross-origin)
 *   - X-Content-Type-Options: set by hosting (nosniff)
 *   - Content-Security-Policy: NOT set by hosting — we add it here (report-only
 *     to start so nothing breaks; promote to enforcing once violation reports
 *     are clean)
 *   - Permissions-Policy: NOT set by hosting — we add it here
 *
 * We also set X-Content-Type-Options / Referrer-Policy defensively in case a
 * response bypasses the CDN (e.g. direct worker URL); the CDN value wins where
 * both are present since it's applied last.
 */
const CSP_REPORT_ONLY = [
  "default-src 'self'",
  // Inline scripts are used for the anti-flash theme script and font loader.
  // 'unsafe-inline' can be tightened later with nonces if desired.
  "script-src 'self' 'unsafe-inline' 'unsafe-eval' https://js.stripe.com https://challenges.cloudflare.com https://www.googletagmanager.com https://www.google-analytics.com https://*.googleapis.com https://*.gstatic.com",
  "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
  "font-src 'self' data: https://fonts.gstatic.com",
  "img-src 'self' data: blob: https:",
  "media-src 'self' blob: https:",
  "connect-src 'self' https://*.supabase.co wss://*.supabase.co https://api.stripe.com https://*.stripe.com https://challenges.cloudflare.com https://*.googleapis.com https://*.google-analytics.com https://*.analytics.google.com https://*.firebaseio.com wss://*.firebaseio.com https://*.firebase.com https://*.livekit.cloud wss://*.livekit.cloud https://*.lovable.app https://*.lovable.dev",
  "frame-src 'self' https://js.stripe.com https://challenges.cloudflare.com https://*.firebaseapp.com",
  "worker-src 'self' blob:",
  "object-src 'none'",
  "base-uri 'self'",
  "form-action 'self'",
  "frame-ancestors 'self'",
].join("; ");

const PERMISSIONS_POLICY = [
  "camera=(self)",
  "microphone=(self)",
  "geolocation=()",
  "payment=(self)",
  "usb=()",
  "magnetometer=()",
  "gyroscope=()",
  "accelerometer=()",
].join(", ");

const securityHeadersMiddleware = createMiddleware().server(async ({ next }) => {
  const result = await next();
  try {
    const response =
      result instanceof Response
        ? result
        : (result as { response?: Response } | undefined)?.response;
    if (response instanceof Response) {
      const contentType = response.headers.get("content-type") ?? "";
      // Set on all responses (cheap, safe) but CSP only matters for documents.
      if (!response.headers.has("x-content-type-options")) {
        response.headers.set("X-Content-Type-Options", "nosniff");
      }
      if (!response.headers.has("referrer-policy")) {
        response.headers.set("Referrer-Policy", "strict-origin-when-cross-origin");
      }
      if (!response.headers.has("permissions-policy")) {
        response.headers.set("Permissions-Policy", PERMISSIONS_POLICY);
      }
      if (contentType.includes("text/html") && !response.headers.has("content-security-policy-report-only")) {
        response.headers.set("Content-Security-Policy-Report-Only", CSP_REPORT_ONLY);
      }
    }
  } catch {
    // Header mutation is best-effort; never block the response.
  }
  return result;
});

const errorMiddleware = createMiddleware().server(async ({ next }) => {
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      // Re-thrown framework errors (redirects, 4xx) still get logged so a
      // failing publish prerender surfaces the real status and reason.
      logServerError(error, { phase: "request-middleware", rethrown: true });
      throw error;
    }
    logServerError(error, { phase: "request-middleware", rethrown: false });
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

export const startInstance = createStart(() => ({
  functionMiddleware: [attachSupabaseAuth],
  // Outermost middleware decorates the final response (including error pages).
  requestMiddleware: [securityHeadersMiddleware, agentDiscoveryMiddleware, errorMiddleware],
}));
